import { routePath } from '@/lib/routes';

/** Kuma reads. Both overridable per environment; defaults are the operator's
 *  monitoring host and the status page slug agreed in the spec (§2). */
export const KUMA_BASE = process.env.STATUS_KUMA_BASE ?? 'https://uptime.monitoring.lab80.net';
export const KUMA_SLUG = process.env.STATUS_KUMA_SLUG ?? 'rii-link';

export type ComponentState = 'operational' | 'degraded' | 'down' | 'maintenance' | 'unmonitored';
export type OverallState = 'operational' | 'degraded' | 'partial' | 'major' | 'maintenance' | 'unknown';

export type StatusComponentDef = {
  id: 'website' | 'dashboard' | 'api' | 'routing' | 'mcp' | 'email';
  label: string;
  description: string;
  /** Exact monitor name on the Kuma status page. Rename here if it changes there. */
  kumaName: string;
};

/** Mapped to the real architecture (CLAUDE.md), not a generic SaaS list.
 *  Routing is the one customers feel first — it drives the "major" state. */
export const COMPONENTS: StatusComponentDef[] = [
  { id: 'website', label: 'Website', description: 'rii.link — this site', kumaName: 'rii.link website' },
  { id: 'dashboard', label: 'Dashboard', description: 'my.rii.link — where links are managed', kumaName: 'rii.link dashboard' },
  { id: 'api', label: 'API — write path', description: 'api.rii.link — creating and editing links, keys, domains', kumaName: 'rii.link backend' },
  { id: 'routing', label: 'Routing service — read path', description: 'the layer that resolves your redirects', kumaName: 'rii.link routing' },
  { id: 'mcp', label: 'MCP server', description: 'mcp.rii.link — agent access', kumaName: 'rii.link mcp' },
  { id: 'email', label: 'Email delivery', description: 'verification, password reset, forwarding', kumaName: 'rii.link mail delivery' },
];

export const ROUTING_COMPONENT_ID: StatusComponentDef['id'] = 'routing';

export const STATE_META: Record<OverallState, { label: string; color: string; tint: string }> = {
  // tint = color at ~8 % over the page color #f8fafc, precomputed so the
  // banner's clearing fill is opaque (translucent tints let grid lines through).
  operational: { label: 'All systems operational', color: 'var(--color-data-green)', tint: '#e5f5ee' },
  degraded: { label: 'Degraded performance', color: 'var(--color-data-amber)', tint: '#f9f1e0' },
  partial: { label: 'Partial outage', color: '#e0661a', tint: '#fbebe0' },
  major: { label: 'Major outage', color: '#d0312d', tint: '#fbe5e4' },
  maintenance: { label: 'Scheduled maintenance', color: 'var(--color-data-blue)', tint: '#e8effb' },
  unknown: { label: 'Status unavailable', color: 'var(--color-dim)', tint: '#eeeef2' },
};

export const COMPONENT_STATE_META: Record<ComponentState, { label: string; color: string }> = {
  operational: { label: 'Operational', color: 'var(--color-data-green)' },
  degraded: { label: 'Degraded', color: 'var(--color-data-amber)' },
  down: { label: 'Down', color: '#d0312d' },
  maintenance: { label: 'Maintenance', color: 'var(--color-data-blue)' },
  unmonitored: { label: 'Not monitored yet', color: 'var(--color-dim)' },
};

export const PROBE_LABEL =
  'Synthetic probe: one request to the routing service every 60 s from our monitoring host. Median and 99th percentile over the last hour.';

export const SLA_HREF = `${routePath('solutionsEnterprise')}#sla`;
export const SECURITY_HREF = routePath('security');
export const DEVELOPERS_WEBHOOKS_HREF = `${routePath('developers')}#ref-webhooks`;
